import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Tag, Users, Globe, RefreshCw, Package, Search } from "lucide-react";
import { LABEL_THEMES, LABEL_COLOR_KEYS, themeFor } from "@/lib/labelColors";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const emptyForm = { name: "", color: "emerald", scope: "all", store_ids: [], sort_order: 0 };

export default function ProductLabelsManagement() {
  const [labels, setLabels] = useState([]);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // 作成/編集ダイアログ
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [storeSearch, setStoreSearch] = useState("");

  // 商品割り当てダイアログ
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLabel, setAssignLabel] = useState(null);
  const [assignSelected, setAssignSelected] = useState(new Set());
  const [assignSearch, setAssignSearch] = useState("");
  const [assignSaving, setAssignSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [labRes, stRes, prodRes] = await Promise.all([
        axios.get(`${API}/product-labels`, getAuthHeaders()),
        axios.get(`${API}/stores`, getAuthHeaders()),
        axios.get(`${API}/products`, getAuthHeaders()),
      ]);
      setLabels(Array.isArray(labRes.data) ? labRes.data : []);
      setStores(Array.isArray(stRes.data) ? stRes.data : []);
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : []);
    } catch (e) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const labelCounts = useMemo(() => {
    const m = {};
    products.forEach((p) => {
      (p.display_labels || []).forEach((n) => {
        m[n] = (m[n] || 0) + 1;
      });
    });
    return m;
  }, [products]);

  const storesMap = useMemo(() => {
    const m = {};
    stores.forEach((s) => (m[s.id] = s));
    return m;
  }, [stores]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sort_order: labels.length });
    setStoreSearch("");
    setDialogOpen(true);
  };

  const openEdit = (lb) => {
    setEditingId(lb.id);
    setForm({
      name: lb.name,
      color: lb.color || "slate",
      scope: lb.scope || "all",
      store_ids: lb.store_ids || [],
      sort_order: lb.sort_order || 0,
    });
    setStoreSearch("");
    setDialogOpen(true);
  };

  const saveLabel = async () => {
    if (!form.name.trim()) {
      toast.error("ラベル名を入力してください");
      return;
    }
    if (form.scope === "selected" && form.store_ids.length === 0) {
      toast.error("表示する顧客を1つ以上選んでください");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        color: form.color,
        scope: form.scope,
        store_ids: form.scope === "selected" ? form.store_ids : [],
        sort_order: Number(form.sort_order) || 0,
      };
      if (editingId) {
        await axios.put(`${API}/admin/product-labels/${editingId}`, payload, getAuthHeaders());
        toast.success("ラベルを更新しました");
      } else {
        await axios.post(`${API}/admin/product-labels`, payload, getAuthHeaders());
        toast.success("ラベルを作成しました");
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const deleteLabel = async (lb) => {
    if (!window.confirm(`ラベル「${lb.name}」を削除します。付与済みの商品からもこのラベルが外れます。よろしいですか？`)) return;
    try {
      await axios.delete(`${API}/admin/product-labels/${lb.id}`, getAuthHeaders());
      toast.success("ラベルを削除しました");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "削除に失敗しました");
    }
  };

  const toggleStoreInForm = (sid) => {
    setForm((f) => {
      const set = new Set(f.store_ids);
      if (set.has(sid)) set.delete(sid);
      else set.add(sid);
      return { ...f, store_ids: [...set] };
    });
  };

  const openAssign = async (lb) => {
    setAssignLabel(lb);
    setAssignSearch("");
    setAssignOpen(true);
    setAssignSelected(new Set());
    try {
      const res = await axios.get(`${API}/admin/product-labels/${lb.id}/products`, getAuthHeaders());
      setAssignSelected(new Set(res.data.product_ids || []));
    } catch (e) {
      // fallback: derive from products
      setAssignSelected(new Set(products.filter((p) => (p.display_labels || []).includes(lb.name)).map((p) => p.id)));
    }
  };

  const toggleAssign = (pid) => {
    setAssignSelected((prev) => {
      const set = new Set(prev);
      if (set.has(pid)) set.delete(pid);
      else set.add(pid);
      return set;
    });
  };

  const saveAssign = async () => {
    if (!assignLabel) return;
    setAssignSaving(true);
    try {
      await axios.put(
        `${API}/admin/product-labels/${assignLabel.id}/products`,
        { product_ids: [...assignSelected] },
        getAuthHeaders()
      );
      toast.success(`「${assignLabel.name}」を ${assignSelected.size} 商品に設定しました`);
      setAssignOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "割り当てに失敗しました");
    } finally {
      setAssignSaving(false);
    }
  };

  const filteredStores = useMemo(() => {
    const q = storeSearch.trim().toLowerCase();
    const list = [...stores].sort((a, b) => (a.code || "").localeCompare(b.code || ""));
    if (!q) return list;
    return list.filter((s) => (s.name || "").toLowerCase().includes(q) || (s.code || "").toLowerCase().includes(q));
  }, [stores, storeSearch]);

  const filteredAssignProducts = useMemo(() => {
    const q = assignSearch.trim().toLowerCase();
    let list = products;
    if (q) {
      list = products.filter(
        (p) => (p.name || "").toLowerCase().includes(q) || (p.code || "").toLowerCase().includes(q)
      );
    }
    // 選択中を先頭に
    return [...list].sort((a, b) => {
      const as = assignSelected.has(a.id) ? 0 : 1;
      const bs = assignSelected.has(b.id) ? 0 : 1;
      if (as !== bs) return as - bs;
      return (a.name || "").localeCompare(b.name || "", "ja");
    }).slice(0, 300);
  }, [products, assignSearch, assignSelected]);

  return (
    <div className="space-y-6" data-testid="product-labels-management">
      <div className="flex items-stretch gap-3">
        <div className="w-1 bg-emerald-600 rounded-sm" />
        <div className="flex-1">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Tag className="h-6 w-6 text-emerald-600" />
            表示ラベル管理
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            「レギュラー商品」「TOP10アイテム」などのラベルを作り、全顧客または選んだ顧客だけに商品を表示できます。ラベルを付けた商品は、履歴やCSV取込に関係なく対象顧客の発注リストに表示されます。
          </p>
        </div>
        <div className="flex items-start gap-2">
          <Button variant="outline" onClick={fetchData} data-testid="labels-refresh" className="h-9">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            更新
          </Button>
          <Button onClick={openCreate} data-testid="label-create-btn" className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white">
            <Plus className="h-4 w-4 mr-1" />
            ラベルを作成
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">読み込み中...</div>
      ) : labels.length === 0 ? (
        <div className="text-center py-12 border border-slate-200 rounded-xl bg-white text-slate-500">
          ラベルがありません。「ラベルを作成」から追加してください。
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {labels.map((lb) => {
            const th = themeFor(lb.color);
            const count = labelCounts[lb.name] || 0;
            return (
              <div
                key={lb.id}
                data-testid={`label-card-${lb.id}`}
                className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-sm font-bold border ${th.badge}`}>
                      {lb.name}
                    </span>
                    {lb.scope === "all" ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                        <Globe className="h-3 w-3" /> 全顧客に表示
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200">
                        <Users className="h-3 w-3" /> 選択顧客のみ（{(lb.store_ids || []).length}店舗）
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openEdit(lb)} data-testid={`label-edit-${lb.id}`} title="編集" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100">
                      <Edit className="h-4 w-4" />
                    </button>
                    <button onClick={() => deleteLabel(lb)} data-testid={`label-delete-${lb.id}`} title="削除" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-red-600 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {lb.scope === "selected" && (lb.store_ids || []).length > 0 && (
                  <div className="text-xs text-slate-500 line-clamp-2">
                    {(lb.store_ids || []).slice(0, 6).map((sid) => storesMap[sid]?.name || sid).join("、")}
                    {(lb.store_ids || []).length > 6 ? " ほか" : ""}
                  </div>
                )}

                <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                  <span className="inline-flex items-center gap-1 text-sm text-slate-600">
                    <Package className="h-4 w-4 text-slate-400" />
                    <span className="font-semibold text-slate-800 tabular-nums">{count}</span> 商品に設定中
                  </span>
                  <Button size="sm" variant="outline" onClick={() => openAssign(lb)} data-testid={`label-assign-${lb.id}`} className="h-8">
                    商品を割り当て
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 作成/編集ダイアログ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="label-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? "ラベルを編集" : "ラベルを作成"}</DialogTitle>
            <DialogDescription>
              ラベル名・色・表示対象（全顧客／選択顧客）を設定します。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm text-slate-700">ラベル名</Label>
              <Input
                data-testid="label-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="例: レギュラー商品 / TOP10アイテム"
                className="mt-1 h-9"
              />
            </div>
            <div>
              <Label className="text-sm text-slate-700">色</Label>
              <div className="flex flex-wrap gap-2 mt-1.5" data-testid="label-color-picker">
                {LABEL_COLOR_KEYS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    data-testid={`label-color-${c}`}
                    className={`h-7 w-7 rounded-full ${LABEL_THEMES[c].dot} transition-all ${
                      form.color === c ? "ring-2 ring-offset-2 ring-slate-800 scale-110" : "opacity-70 hover:opacity-100"
                    }`}
                    aria-label={c}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm text-slate-700">表示対象</Label>
              <div className="flex gap-2 mt-1.5">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, scope: "all" })}
                  data-testid="label-scope-all"
                  className={`flex-1 flex items-center justify-center gap-1.5 h-10 rounded-lg border text-sm font-medium transition-colors ${
                    form.scope === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <Globe className="h-4 w-4" /> 全顧客に表示
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, scope: "selected" })}
                  data-testid="label-scope-selected"
                  className={`flex-1 flex items-center justify-center gap-1.5 h-10 rounded-lg border text-sm font-medium transition-colors ${
                    form.scope === "selected" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <Users className="h-4 w-4" /> 選択顧客のみ
                </button>
              </div>
            </div>

            {form.scope === "selected" && (
              <div data-testid="label-store-picker">
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-sm text-slate-700">表示する顧客（{form.store_ids.length} 選択中）</Label>
                  <button type="button" className="text-xs text-indigo-600 hover:underline" onClick={() => setForm({ ...form, store_ids: [] })}>
                    すべて解除
                  </button>
                </div>
                <div className="relative mb-2">
                  <Search className="h-4 w-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                  <Input value={storeSearch} onChange={(e) => setStoreSearch(e.target.value)} placeholder="店舗名・コードで検索" className="h-9 pl-8" />
                </div>
                <div className="max-h-52 overflow-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {filteredStores.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                      <Checkbox
                        checked={form.store_ids.includes(s.id)}
                        onCheckedChange={() => toggleStoreInForm(s.id)}
                        data-testid={`label-store-opt-${s.id}`}
                      />
                      <span className="text-sm text-slate-700">{s.name}</span>
                      <span className="text-[10px] text-slate-400 font-mono ml-auto">{s.code}</span>
                    </label>
                  ))}
                  {filteredStores.length === 0 && (
                    <div className="px-3 py-4 text-center text-sm text-slate-400">該当する店舗がありません</div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button onClick={saveLabel} disabled={saving} data-testid="label-save-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? "保存中..." : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 商品割り当てダイアログ */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="label-assign-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              商品を割り当て
              {assignLabel && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-sm font-bold border ${themeFor(assignLabel.color).badge}`}>
                  {assignLabel.name}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              このラベルを付ける商品を検索して選び、保存してください。指定した商品にラベルが付与され、外した商品からは解除されます。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="flex items-center justify-between gap-3">
              <div className="relative flex-1">
                <Search className="h-4 w-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <Input
                  value={assignSearch}
                  onChange={(e) => setAssignSearch(e.target.value)}
                  placeholder="商品名・コードで検索"
                  data-testid="assign-search"
                  className="h-9 pl-8"
                />
              </div>
              <span className="text-sm text-slate-600 shrink-0">
                選択中 <span className="font-semibold text-slate-900 tabular-nums">{assignSelected.size}</span>
              </span>
            </div>
            <div className="max-h-[50vh] overflow-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {filteredAssignProducts.map((p) => (
                <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                  <Checkbox
                    checked={assignSelected.has(p.id)}
                    onCheckedChange={() => toggleAssign(p.id)}
                    data-testid={`assign-product-${p.id}`}
                  />
                  <span className="text-sm text-slate-800 flex-1">{p.name}</span>
                  <span className="text-[11px] text-slate-500">{p.unit}</span>
                  <span className="text-[10px] text-slate-400 font-mono w-20 text-right">{p.code}</span>
                </label>
              ))}
              {filteredAssignProducts.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-slate-400">該当する商品がありません</div>
              )}
            </div>
            {assignSearch.trim() === "" && products.length > 300 && (
              <p className="text-[11px] text-slate-400">※ 先頭300件を表示中。検索で絞り込めます（選択済みは先頭に表示されます）。</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAssignOpen(false)}>キャンセル</Button>
            <Button onClick={saveAssign} disabled={assignSaving} data-testid="assign-save-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {assignSaving ? "保存中..." : "この内容で保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
